import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { registerLocale } from "react-datepicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptb from "date-fns/locale/pt-BR";
import en from "date-fns/locale/en-US";
import es from "date-fns/locale/es";
registerLocale("pt-BR", ptb);
registerLocale("en-US", en);
registerLocale("es", es);
registerLocale("it-IT", it);
import DateHelper from "../../helpers/DateHelper";
import it from "date-fns/locale/it";
import { useStorefrontSettings } from '../../hooks/StorefrontSettingsContext';
import { createTheme, ThemeProvider } from "@mui/material/styles"; 



const getDateFormat = (propsDateFormat = "MM/dd/yyyy") => {
  return typeof propsDateFormat === "string" ? propsDateFormat : "MM/dd/yyyy";
};


const calendarTheme = createTheme({
    zIndex: {
        modal: 2000
    },
});


const Calendar = (props) => {
  const storefrontSettings = useStorefrontSettings();
  const rootMinDate = new Date(props.minDate);
  rootMinDate.setHours(0, 0, 0, 0);

  const rootMax = props.maxDate
    ? new Date(new Date(props.maxDate).setHours(0, 0, 0, 0))
    : null;

  const dateInput = props.date ? new Date(props.date) : null;
  const calendarSelectedDate =
    dateInput && dateInput >= rootMinDate ? dateInput : null;

  const dateInputChanged = (date, e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    if (!DateHelper.isValidDate(date)) {
      return props.callBackFunction(null);
    }

    if (!rootMinDate || date >= rootMinDate) {
      let _d = new Date(date);
      _d.setHours(0, 0, 0, 0);
      return props.callBackFunction(_d);
    }
  };

  const disabledDatesSet = useMemo(() => {
    return new Set(
      (props.disabledDates || []).map((d) => {
        const dt = new Date(d);
        dt.setHours(0, 0, 0, 0);
        return dt.getTime();
      })
    );
  }, [props.disabledDates]);

  const shouldDisableDate = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    const time = normalized.getTime();

    if (disabledDatesSet.size > 0) {
      return disabledDatesSet.has(time);
    }

    if (props.filterWeekends) {
      return !isWeekday(date);
    }

    return false;
  };

  const isWeekday = (date) => {
    if (props.filterWeekends) {
      const day = date.getDay();

      return day !== 0 && day !== 6;
    } else {
      return true;
    }
  };


    return (
        <div
            className="fele-calendar"
            style={props.disabled ? { pointerEvents: "none", opacity: "0.4" } : {}}
        >
            <label className="datepicker-wrapper" style={{ width: "100%" }}>
                <div className="col-md-12">
                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={storefrontSettings.locale}>
                        <ThemeProvider theme={calendarTheme}>
                            <DatePicker
                                className="custom-form-controls"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        inputProps: { style: { fontSize: 14, color: props.color ?? "black" } },
                                        error:
                                            props.needsValidated &&
                                            (!calendarSelectedDate || calendarSelectedDate === rootMinDate || props.dateError),
                                        clearable: props.clearable,
                                    },
                                    inputAdornment: { position: props.showIcon ? "start" : "none", sx: { marginLeft: "8px" } },
                                    popper: {
                                        modifiers: [
                                            {
                                                name: "zIndex",
                                                enabled: true,
                                                phase: "write",
                                                fn: ({ state }) => {
                                                    state.styles.popper.zIndex = 2001;
                                                },
                                            },
                                        ],
                                    },
                                    desktopPaper: { sx: { zIndex: 2002 } },
                                }}
                                sx={{
                                    "& .MuiInputBase-root": {
                                        border: "1px solid #dbdbdb !important",
                                        color: "#272727",
                                        fontSize: "1.4rem",
                                        padding: "1rem",
                                        boxSizing: "border-box",
                                        width: "100%",
                                        height: "3.5rem",
                                        "& .MuiInputBase-input": { border: "0!important" },
                                        "& .clearButton": { marginRight: "8px" },
                                        "& .MuiSvgIcon-root": { color: props.color ?? "black" },
                                    },
                                }}
                                value={calendarSelectedDate}
                                onChange={dateInputChanged}
                                minDate={rootMinDate}
                                maxDate={rootMax}
                                shouldDisableDate={shouldDisableDate}
                                format={getDateFormat(props.dateFormat)}
                                loading={props.loading}
                            />
                        </ThemeProvider>
                    </LocalizationProvider>
                </div>
            </label>
        </div>
    );
};


Calendar.propTypes = {
  date: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  clearable: PropTypes.bool,
  disabledDates: PropTypes.array,
  loading: PropTypes.bool,
};

Calendar.defaultProps = {
  clearable: false,
  loading: false,
};

export default Calendar;
